/**
 * Shop the look — hotspot ↔ product list sync, product-page slider, compact tooltips.
 *
 * Tooltip visibility: CSS (:hover, :focus-within, .is-active on mobile).
 * Tooltip position: JS clamps to viewport and flips below when needed.
 * Product pages: shared `slideshow-component` (same drag/arrows/counter as cart upsell + pairs-well-with).
 * Desktop page size 1–4 from scroller height (ResizeObserver); mobile fixed at 2 (Figma).
 */
const TOOLTIP_GAP_PX = 4;
const VIEWPORT_EDGE_PX = 16;
const TOOLTIP_MAX_WIDTH_MOBILE_PX = 200;
const TOOLTIP_MIN_WIDTH_MOBILE_PX = 140;
const TOOLTIP_HEIGHT_MOBILE_PX = 72;
const TOOLTIP_MAX_WIDTH_DESKTOP_PX = 193;
const TOOLTIP_MIN_WIDTH_DESKTOP_PX = 193;
const TOOLTIP_HEIGHT_DESKTOP_PX = 72;
const TOOLTIP_DISMISS_MS = 150;
const STL_DESKTOP_MQ = matchMedia('(min-width: 990px)');
const STL_ROW_HEIGHT_PX = 116;
const STL_ROW_GAP_PX = 16;
const STL_MOBILE_PAGE_SIZE = 2;
const STL_MAX_PAGE_SIZE = 4;

/** @param {number} value @param {number} min @param {number} max */
function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

class DevShopTheLook extends HTMLElement {
  /** @type {string | null} */
  #activeIndex = null;

  /** @type {number} */
  #pageIndex = 0;

  /** @type {number} */
  #pageSize = STL_MAX_PAGE_SIZE;

  /** @type {(() => void) | null} */
  #resizeHandler = null;

  /** @type {(() => void) | null} */
  #scrollHandler = null;

  /** @type {(() => void) | null} */
  #pendingDismissCleanup = null;

  /** @type {ResizeObserver | null} */
  #viewportObserver = null;

  /** @type {number | null} */
  #layoutFrame = null;

  /** @type {string} */
  #layoutKey = '';

  connectedCallback() {
    this.#bind();
    this.#observeProductsViewport();
    this.#layoutProducts();
    STL_DESKTOP_MQ.addEventListener('change', this.#onBreakpointChange);
    this.#resizeHandler = () => this.#positionActiveTooltip();
    this.#scrollHandler = () => this.#onScroll();
    window.addEventListener('resize', this.#resizeHandler, { passive: true });
    document.addEventListener('scroll', this.#scrollHandler, { passive: true, capture: true });
  }

  disconnectedCallback() {
    this.#unbind();
    this.#viewportObserver?.disconnect();
    this.#viewportObserver = null;
    if (this.#layoutFrame !== null) {
      cancelAnimationFrame(this.#layoutFrame);
      this.#layoutFrame = null;
    }
    STL_DESKTOP_MQ.removeEventListener('change', this.#onBreakpointChange);
    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
      this.#resizeHandler = null;
    }
    if (this.#scrollHandler) {
      document.removeEventListener('scroll', this.#scrollHandler, { capture: true });
      this.#scrollHandler = null;
    }
    this.#cancelPendingDismiss();
    this.#resetAllTooltipPositions();
  }

  #onBreakpointChange = () => {
    this.#setActive(null);
    this.#layoutProducts();
  };

  #bind() {
    this.addEventListener('pointerenter', this.#onPointerEnter, true);
    this.addEventListener('pointerleave', this.#onPointerLeave, true);
    this.addEventListener('focusin', this.#onFocusIn, true);
    this.addEventListener('focusout', this.#onFocusOut, true);
    this.addEventListener('click', this.#onClick, true);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('pointerdown', this.#onPointerDown, true);
  }

  #unbind() {
    this.removeEventListener('pointerenter', this.#onPointerEnter, true);
    this.removeEventListener('pointerleave', this.#onPointerLeave, true);
    this.removeEventListener('focusin', this.#onFocusIn, true);
    this.removeEventListener('focusout', this.#onFocusOut, true);
    this.removeEventListener('click', this.#onClick, true);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('pointerdown', this.#onPointerDown, true);
  }

  /** @returns {HTMLElement[]} */
  #getProductRows() {
    return Array.from(this.querySelectorAll('[data-stl-product-row]'));
  }

  /** @returns {HTMLElement | null} */
  #getProductsSlideshow() {
    const slideshow = this.querySelector('[data-stl-products-slideshow]');
    return slideshow instanceof HTMLElement ? slideshow : null;
  }

  /** @returns {HTMLElement | null} */
  #getProductsScroller() {
    const scroller = this.#getProductsSlideshow()?.querySelector('slideshow-slides');
    return scroller instanceof HTMLElement ? scroller : null;
  }

  /** @returns {HTMLElement[]} */
  #getProductSlides() {
    const scroller = this.#getProductsScroller();
    if (!scroller) return [];
    return Array.from(scroller.querySelectorAll(':scope > [data-stl-products-slide]'));
  }

  /** @returns {number} */
  #getPageCount() {
    return Math.max(1, this.#getProductSlides().length);
  }

  #observeProductsViewport() {
    this.#viewportObserver?.disconnect();

    const scroller = this.#getProductsScroller();
    if (!(scroller instanceof HTMLElement) || typeof ResizeObserver === 'undefined') return;

    this.#viewportObserver = new ResizeObserver(() => {
      // Mobile page size is fixed; only desktop remeasures from scroller height.
      if (this.#isMobileLayout()) return;
      this.#scheduleProductsLayout();
    });
    this.#viewportObserver.observe(scroller);
  }

  #scheduleProductsLayout() {
    if (this.#layoutFrame !== null) {
      cancelAnimationFrame(this.#layoutFrame);
    }

    this.#layoutFrame = requestAnimationFrame(() => {
      this.#layoutFrame = null;
      this.#layoutProducts();
    });
  }

  /** @returns {number} */
  #measurePageSize() {
    if (this.#isMobileLayout()) return STL_MOBILE_PAGE_SIZE;

    const scroller = this.#getProductsScroller();
    if (!(scroller instanceof HTMLElement)) return STL_MAX_PAGE_SIZE;

    const height = scroller.clientHeight;
    if (height <= 0) return STL_MAX_PAGE_SIZE;

    return clamp(
      Math.floor((height + STL_ROW_GAP_PX) / (STL_ROW_HEIGHT_PX + STL_ROW_GAP_PX)),
      1,
      STL_MAX_PAGE_SIZE
    );
  }

  /**
   * Chunk rows into slideshow slides. Mobile: 2 per page. Desktop: 1–4 from scroller height.
   * Nav + drag + counter come from slideshow-component (same as upsell / pairs-well-with).
   */
  #layoutProducts() {
    const slideshow = this.#getProductsSlideshow();
    const scroller = this.#getProductsScroller();
    const nav = this.querySelector('[data-stl-products-nav]');
    if (!(slideshow instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

    const rows = this.#getProductRows();
    if (rows.length === 0) return;

    const pageSize = this.#measurePageSize();
    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const layoutKey = `${this.#isMobileLayout() ? 'm' : 'd'}:${pageSize}:${rows.length}`;
    const pageSizeChanged = pageSize !== this.#pageSize;

    if (this.#layoutKey === layoutKey) return;

    const slideshowCurrent = Number(/** @type {any} */ (slideshow).current);
    if (Number.isFinite(slideshowCurrent)) {
      this.#pageIndex = slideshowCurrent;
    }

    this.#layoutKey = layoutKey;
    this.#pageSize = pageSize;
    this.#ensureProductSlides(scroller, pageCount);

    const slides = this.#getProductSlides();
    for (let page = 0; page < pageCount; page++) {
      const chunk = rows.slice(page * pageSize, (page + 1) * pageSize);
      this.#fillProductSlide(slides[page], chunk, page);
    }

    if (nav) {
      if (pageCount > 1) {
        nav.removeAttribute('hidden');
      } else {
        nav.setAttribute('hidden', '');
      }
    }

    this.#pageIndex = clamp(this.#pageIndex, 0, pageCount - 1);
    this.#syncProductsSlideshow(this.#pageIndex);

    if (pageSizeChanged && this.#activeIndex !== null) {
      this.#revealProductsPageForIndex(this.#activeIndex);
    }
  }

  /**
   * @param {HTMLElement} scroller
   * @param {number} pageCount
   */
  #ensureProductSlides(scroller, pageCount) {
    let slides = this.#getProductSlides();

    while (slides.length < pageCount) {
      const slide = document.createElement('slideshow-slide');
      slide.setAttribute('ref', 'slides[]');
      slide.className = 'dev-shop-the-look__products-slide';
      slide.setAttribute('data-stl-products-slide', '');
      slide.setAttribute('aria-hidden', 'true');

      const list = document.createElement('ul');
      list.className = 'dev-shop-the-look__products';
      list.setAttribute('data-stl-products-list', '');
      list.setAttribute('role', 'list');
      slide.appendChild(list);

      scroller.appendChild(slide);
      slides = this.#getProductSlides();
    }

    while (slides.length > pageCount) {
      slides.pop()?.remove();
      slides = this.#getProductSlides();
    }
  }

  /**
   * @param {HTMLElement | undefined} slide
   * @param {HTMLElement[]} rows
   * @param {number} pageIndex
   */
  #fillProductSlide(slide, rows, pageIndex) {
    if (!(slide instanceof HTMLElement)) return;

    slide.setAttribute('data-stl-page-index', String(pageIndex));

    let list = slide.querySelector('[data-stl-products-list]');
    if (!(list instanceof HTMLElement)) {
      list = document.createElement('ul');
      list.className = 'dev-shop-the-look__products';
      list.setAttribute('data-stl-products-list', '');
      list.setAttribute('role', 'list');
      slide.appendChild(list);
    }

    for (const row of rows) {
      list.appendChild(row);
    }
  }

  /**
   * Refresh slideshow refs after slide create/remove, then select the page.
   * @param {number} index
   */
  #syncProductsSlideshow(index) {
    const slideshow = this.#getProductsSlideshow();
    if (!slideshow) return;

    const pageCount = this.#getPageCount();
    this.#pageIndex = clamp(index, 0, pageCount - 1);

    const slideshowAny = /** @type {any} */ (slideshow);
    slideshowAny.updatedCallback?.();

    // Keep drag/scroll gated on multi-page (auto-hide-controls may leave a stale disabled flag).
    slideshowAny.disabled = pageCount <= 1;

    if (typeof slideshowAny.select === 'function') {
      slideshowAny.select(this.#pageIndex, undefined, { animate: false });
    }

    slideshowAny.refreshPageCounter?.();
  }

  /** @param {number} index */
  #goToProductsPage(index) {
    const pageCount = this.#getPageCount();
    if (pageCount <= 1) return;

    this.#pageIndex = clamp(index, 0, pageCount - 1);
    const slideshowAny = /** @type {any} */ (this.#getProductsSlideshow());
    slideshowAny?.select?.(this.#pageIndex);
  }

  /**
   * Show the slide that contains the product index (desktop + mobile slider).
   * @param {string | null} productIndex
   */
  #revealProductsPageForIndex(productIndex) {
    if (productIndex === null) return;

    const row = this.querySelector(`[data-stl-product-row][data-stl-index="${productIndex}"]`);
    const slide = row?.closest('[data-stl-products-slide]');
    if (!(slide instanceof HTMLElement)) return;

    const slides = this.#getProductSlides();
    const pageIndex = slides.indexOf(slide);
    if (pageIndex < 0) return;

    this.#goToProductsPage(pageIndex);
  }

  #resetAllTooltipPositions() {
    for (const hotspot of this.querySelectorAll('[data-stl-hotspot]')) {
      this.#resetTooltipPosition(hotspot);
    }
  }

  /** @param {Element} hotspot */
  #resetTooltipPosition(hotspot) {
    const tooltip = hotspot.querySelector('[data-stl-tooltip]');
    if (!(tooltip instanceof HTMLElement)) return;

    tooltip.style.position = '';
    tooltip.style.left = '';
    tooltip.style.top = '';
    tooltip.style.bottom = '';
    tooltip.style.transform = '';
    tooltip.style.width = '';
    tooltip.style.maxWidth = '';
    tooltip.style.visibility = '';

    if (hotspot instanceof HTMLElement) {
      hotspot.removeAttribute('data-stl-placement');
    }
  }

  /** @returns {boolean} */
  #isMobileLayout() {
    return !STL_DESKTOP_MQ.matches;
  }

  /**
   * @param {DOMRect} rect
   * @param {number} viewportWidth
   * @param {number} viewportHeight
   * @param {number} edge
   */
  #placementOverflow(rect, viewportWidth, viewportHeight, edge) {
    const overflowLeft = Math.max(0, edge - rect.left);
    const overflowRight = Math.max(0, rect.right - (viewportWidth - edge));
    const overflowTop = Math.max(0, edge - rect.top);
    const overflowBottom = Math.max(0, rect.bottom - (viewportHeight - edge));

    return overflowLeft + overflowRight + overflowTop + overflowBottom;
  }

  #cancelPendingDismiss() {
    if (this.#pendingDismissCleanup) {
      this.#pendingDismissCleanup();
      this.#pendingDismissCleanup = null;
    }
  }

  #getTooltipMaxWidth() {
    const preferredMax = this.#isMobileLayout()
      ? TOOLTIP_MAX_WIDTH_MOBILE_PX
      : TOOLTIP_MAX_WIDTH_DESKTOP_PX;
    const preferredMin = this.#isMobileLayout()
      ? TOOLTIP_MIN_WIDTH_MOBILE_PX
      : TOOLTIP_MIN_WIDTH_DESKTOP_PX;
    const viewportMax = window.innerWidth - VIEWPORT_EDGE_PX * 2;
    return Math.min(preferredMax, Math.max(preferredMin, viewportMax));
  }

  #getTooltipHeight() {
    return this.#isMobileLayout() ? TOOLTIP_HEIGHT_MOBILE_PX : TOOLTIP_HEIGHT_DESKTOP_PX;
  }

  /** @param {HTMLElement} tooltip */
  #measureTooltip(tooltip) {
    const maxWidth = this.#getTooltipMaxWidth();

    tooltip.style.visibility = 'hidden';
    tooltip.style.opacity = '1';
    tooltip.style.position = 'fixed';
    tooltip.style.left = '0';
    tooltip.style.top = '0';
    tooltip.style.bottom = 'auto';
    tooltip.style.transform = 'none';
    tooltip.style.width = `${maxWidth}px`;
    tooltip.style.maxWidth = `${maxWidth}px`;

    tooltip.getBoundingClientRect();

    tooltip.style.visibility = '';
    tooltip.style.opacity = '';

    return {
      width: maxWidth,
      height: this.#getTooltipHeight(),
    };
  }

  /** @param {HTMLElement} hotspot */
  #positionTooltip(hotspot) {
    const tooltip = hotspot.querySelector('[data-stl-tooltip]');
    const trigger = hotspot.querySelector('[data-stl-hotspot-trigger]');
    if (!(tooltip instanceof HTMLElement) || !(trigger instanceof HTMLElement)) return;

    const triggerRect = trigger.getBoundingClientRect();
    const { width: tooltipWidth, height: tooltipHeight } = this.#measureTooltip(tooltip);

    const edge = VIEWPORT_EDGE_PX;
    const gap = TOOLTIP_GAP_PX;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxLeft = viewportWidth - tooltipWidth - edge;
    const maxTop = viewportHeight - tooltipHeight - edge;

    /** @type {{ placement: string, left: number, top: number, overflow: number }[]} */
    let candidates;

    if (this.#isMobileLayout()) {
      candidates = [
        {
          placement: 'right',
          left: triggerRect.right + gap,
          top: triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2,
        },
        {
          placement: 'left',
          left: triggerRect.left - tooltipWidth - gap,
          top: triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2,
        },
        {
          placement: 'bottom',
          left: triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2,
          top: triggerRect.bottom + gap,
        },
        {
          placement: 'top',
          left: triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2,
          top: triggerRect.top - tooltipHeight - gap,
        },
      ];
    } else {
      candidates = [
        {
          placement: 'top',
          left: triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2,
          top: triggerRect.top - tooltipHeight - gap,
        },
        {
          placement: 'bottom',
          left: triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2,
          top: triggerRect.bottom + gap,
        },
        {
          placement: 'right',
          left: triggerRect.right + gap,
          top: triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2,
        },
        {
          placement: 'left',
          left: triggerRect.left - tooltipWidth - gap,
          top: triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2,
        },
      ];
    }

    let best = candidates[0];

    for (const candidate of candidates) {
      const left = clamp(candidate.left, edge, maxLeft);
      const top = clamp(candidate.top, edge, maxTop);
      const overflow = this.#placementOverflow(
        new DOMRect(left, top, tooltipWidth, tooltipHeight),
        viewportWidth,
        viewportHeight,
        edge
      );

      const bestOverflow = this.#placementOverflow(
        new DOMRect(
          clamp(best.left, edge, maxLeft),
          clamp(best.top, edge, maxTop),
          tooltipWidth,
          tooltipHeight
        ),
        viewportWidth,
        viewportHeight,
        edge
      );

      if (overflow < bestOverflow) {
        best = candidate;
      }
    }

    const left = clamp(best.left, edge, maxLeft);
    const top = clamp(best.top, edge, maxTop);

    tooltip.style.position = 'fixed';
    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.top = `${Math.round(top)}px`;
    tooltip.style.bottom = 'auto';
    tooltip.style.transform = 'none';
    tooltip.style.width = `${tooltipWidth}px`;
    tooltip.style.maxWidth = `${tooltipWidth}px`;
    hotspot.dataset.stlPlacement = best.placement;
  }

  #positionActiveTooltip() {
    if (this.#activeIndex === null) return;

    const hotspot = this.querySelector(
      `.dev-shop-the-look__hotspot[data-stl-index="${this.#activeIndex}"]`
    );
    if (hotspot instanceof HTMLElement && hotspot.hasAttribute('data-stl-placement')) {
      this.#positionTooltip(hotspot);
    }
  }

  /** @param {HTMLElement} hotspot @param {boolean} visible */
  #setHotspotPreviewAccessibility(hotspot, visible) {
    const preview = hotspot.querySelector('[data-stl-tooltip]');
    const previewLink = preview?.querySelector('[data-stl-tooltip-link]');
    const trigger = hotspot.querySelector('[data-stl-hotspot-trigger]');

    if (preview instanceof HTMLElement) {
      preview.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    if (previewLink instanceof HTMLAnchorElement) {
      if (visible) {
        previewLink.removeAttribute('tabindex');
      } else {
        previewLink.setAttribute('tabindex', '-1');
      }
    }

    if (trigger instanceof HTMLButtonElement) {
      trigger.setAttribute('aria-expanded', visible ? 'true' : 'false');
    }
  }

  /** @param {string | null} index */
  #syncActiveState(index) {
    this.querySelectorAll('[data-stl-index]').forEach((node) => {
      const isActive = index !== null && node.getAttribute('data-stl-index') === index;
      node.classList.toggle('is-active', isActive);

      if (node.classList.contains('dev-shop-the-look__product-row')) {
        if (isActive) {
          node.setAttribute('aria-current', 'true');
        } else {
          node.removeAttribute('aria-current');
        }
      }

      if (node.classList.contains('dev-shop-the-look__hotspot')) {
        if (this.#isMobileLayout()) {
          this.#setHotspotPreviewAccessibility(node, isActive);
        } else if (!isActive) {
          this.#setHotspotPreviewAccessibility(node, false);
        }
      }
    });
  }

  #clearActiveImmediate() {
    this.#cancelPendingDismiss();

    if (this.#activeIndex === null) return;

    const hotspot = this.querySelector(
      `.dev-shop-the-look__hotspot[data-stl-index="${this.#activeIndex}"]`
    );

    this.#activeIndex = null;
    this.#syncActiveState(null);

    if (hotspot instanceof HTMLElement) {
      this.#resetTooltipPosition(hotspot);
    }
  }

  #onScroll = () => {
    if (!this.#isMobileLayout() || this.#activeIndex === null) return;
    this.#clearActiveImmediate();
  };

  #dismissActive() {
    const previousIndex = this.#activeIndex;
    if (previousIndex === null) return;

    const hotspot = this.querySelector(
      `.dev-shop-the-look__hotspot[data-stl-index="${previousIndex}"]`
    );

    this.#activeIndex = null;
    this.#syncActiveState(null);

    if (!(hotspot instanceof HTMLElement)) return;

    const tooltip = hotspot.querySelector('[data-stl-tooltip]');
    const cleanup = () => {
      this.#resetTooltipPosition(hotspot);
      if (this.#pendingDismissCleanup === cleanup) {
        this.#pendingDismissCleanup = null;
      }
    };

    if (!(tooltip instanceof HTMLElement) || !hotspot.hasAttribute('data-stl-placement')) {
      cleanup();
      return;
    }

    if (!this.#isMobileLayout()) {
      cleanup();
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      cleanup();
      return;
    }

    /** @type {number | undefined} */
    let fallbackTimer;

    const finish = () => {
      if (fallbackTimer !== undefined) {
        window.clearTimeout(fallbackTimer);
      }
      cleanup();
    };

    this.#pendingDismissCleanup = finish;

    tooltip.addEventListener(
      'transitionend',
      (event) => {
        if (event.target !== tooltip || event.propertyName !== 'opacity') return;
        finish();
      },
      { once: true }
    );

    fallbackTimer = window.setTimeout(finish, TOOLTIP_DISMISS_MS + 50);
  }

  /** @param {string | null} index */
  #setActive(index) {
    this.#cancelPendingDismiss();

    if (index === null) {
      this.#dismissActive();
      return;
    }

    if (this.#activeIndex !== null && this.#activeIndex !== index) {
      const previousHotspot = this.querySelector(
        `.dev-shop-the-look__hotspot[data-stl-index="${this.#activeIndex}"]`
      );
      if (previousHotspot instanceof HTMLElement) {
        this.#resetTooltipPosition(previousHotspot);
      }
    }

    this.#activeIndex = index;
    this.#syncActiveState(index);
    this.#revealProductsPageForIndex(index);

    const hotspot = this.querySelector(
      `.dev-shop-the-look__hotspot[data-stl-index="${index}"]`
    );
    if (hotspot instanceof HTMLElement && this.#isMobileLayout()) {
      this.#positionTooltip(hotspot);
    }
  }

  /** @param {Event} event */
  #indexFromTarget(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    const node = target.closest('[data-stl-index]');
    if (!(node instanceof HTMLElement) || !this.contains(node)) return null;

    return node.getAttribute('data-stl-index');
  }

  /** @param {Element | null | undefined} element */
  #indexFromElement(element) {
    if (!(element instanceof Element)) return null;

    const node = element.closest('[data-stl-index]');
    if (!(node instanceof HTMLElement) || !this.contains(node)) return null;

    return node.getAttribute('data-stl-index');
  }

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (this.#isMobileLayout()) return;

    const index = this.#indexFromTarget(event);
    if (index === null) return;

    const target = event.target;
    if (target instanceof Element) {
      const enteredHotspot = target.closest('.dev-shop-the-look__hotspot');
      if (
        enteredHotspot instanceof HTMLElement &&
        enteredHotspot.getAttribute('data-stl-index') === index
      ) {
        this.#positionTooltip(enteredHotspot);
        this.#setHotspotPreviewAccessibility(enteredHotspot, true);
      }
    }

    this.#setActive(index);
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (this.#isMobileLayout()) return;

    const relatedTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    if (relatedTarget && this.contains(relatedTarget)) {
      const fromIndex = this.#indexFromTarget(event);
      if (fromIndex === null) return;

      const relatedIndex = this.#indexFromElement(relatedTarget);
      const fromHotspotEl =
        event.target instanceof Element ? event.target.closest('.dev-shop-the-look__hotspot') : null;
      const toHotspotEl = relatedTarget.closest('.dev-shop-the-look__hotspot');

      if (fromHotspotEl && toHotspotEl === fromHotspotEl) {
        return;
      }

      if (relatedIndex === fromIndex) {
        const toRow = relatedTarget.closest('.dev-shop-the-look__product-row');

        if (fromHotspotEl && toRow) {
          const hotspot = this.querySelector(
            `.dev-shop-the-look__hotspot[data-stl-index="${fromIndex}"]`
          );
          if (hotspot instanceof HTMLElement) {
            this.#resetTooltipPosition(hotspot);
            this.#setHotspotPreviewAccessibility(hotspot, false);
          }
        }

        return;
      }

      if (this.#activeIndex !== fromIndex) {
        const hotspot = this.querySelector(
          `.dev-shop-the-look__hotspot[data-stl-index="${fromIndex}"]`
        );
        if (hotspot instanceof HTMLElement) {
          this.#resetTooltipPosition(hotspot);
          this.#setHotspotPreviewAccessibility(hotspot, false);
        }
      }

      if (this.#activeIndex === fromIndex) {
        this.#setActive(null);
      }

      return;
    }

    this.#setActive(null);
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (this.#isMobileLayout()) return;

    const index = this.#indexFromTarget(event);
    if (index === null) return;

    this.#setActive(index);

    if (!(event.target instanceof Element)) return;

    const hotspot = event.target.closest('.dev-shop-the-look__hotspot');
    if (hotspot instanceof HTMLElement && hotspot.getAttribute('data-stl-index') === index) {
      this.#positionTooltip(hotspot);
      this.#setHotspotPreviewAccessibility(hotspot, true);
    }
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (this.#isMobileLayout()) return;

    const related = event.relatedTarget;
    if (related instanceof Element && this.contains(related)) return;

    this.#setActive(null);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('.dev-shop-the-look__atc, .quick-add, .dev-shop-the-look__tooltip-link')) {
      return;
    }

    if (target.closest('[data-stl-tooltip]')) {
      return;
    }

    const hotspotTrigger = target.closest('[data-stl-hotspot-trigger]');
    if (hotspotTrigger instanceof HTMLButtonElement) {
      const hotspot = hotspotTrigger.closest('.dev-shop-the-look__hotspot');
      if (!(hotspot instanceof HTMLElement)) return;

      const index = hotspot.getAttribute('data-stl-index');
      if (index === null) return;

      if (this.#isMobileLayout()) {
        event.preventDefault();
        event.stopPropagation();

        if (this.#activeIndex === index) {
          this.#setActive(null);
        } else {
          this.#setActive(index);
        }
      }
      return;
    }

    if (this.#activeIndex !== null && target.closest('[data-stl-scene]')) {
      this.#setActive(null);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.contains(document.activeElement) && this.#activeIndex === null) return;

    this.#setActive(null);
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (this.contains(event.target)) return;

    this.#setActive(null);
  };
}

if (!customElements.get('dev-shop-the-look')) {
  customElements.define('dev-shop-the-look', DevShopTheLook);
}
